import { canAcceptPayments, paymentProcessor, hasStripeAccountConflict, getStripeAccountId } from '../../utils/paymentReadiness';

export const STRIPE_SETUP = Object.freeze({
  CHECKING: 'checking', NOT_STARTED: 'not_started', INCOMPLETE: 'incomplete', ACTION_REQUIRED: 'action_required',
  SUBMITTED: 'submitted', REVIEW_PENDING: 'review_pending', APPROVAL_SYNCING: 'approval_syncing',
  SYNC_DELAYED: 'sync_delayed', UNAVAILABLE: 'unavailable', RESTRICTED: 'restricted',
  PAYOUT_PENDING: 'payout_pending', READY: 'ready',
});
export const APPROVAL_SYNC_GRACE_MS = 60_000;
export const STATUS_STALE_AFTER_MS = 120_000;

export function getStripeExitState(status) {
  if (status?.stripeApiAvailable !== true) return 'checking';
  if (status.requirementsCurrentlyDue?.length || status.requirementsPastDue?.length) return 'incomplete';
  if (status.chargesEnabled === true) return 'approved';
  if (status.requirementsDisabledReason && status.requirementsDisabledReason !== 'requirements.pending_verification') return 'incomplete';
  if (status.detailsSubmitted === true || status.requirementsPendingVerification?.length) return 'submitted';
  return 'incomplete';
}

/** Payment access, payout readiness, requirements and freshness are independent. */
export function getStripeSetupState({ merchant, liveStatus, setupStarted = false, checkFailed = false, syncDelayed = false, initialCheckPending = false, now = Date.now() }) {
  const storedReady = canAcceptPayments(merchant);
  const isFinix = paymentProcessor(merchant) === 'FINIX';
  const conflict = !isFinix && hasStripeAccountConflict(merchant);
  const snapshot = merchant?.stripeDetails?.statusSnapshot ?? {};
  const read = (key, fallback = null) => liveStatus?.[key] !== undefined ? liveStatus[key] : snapshot[key] !== undefined ? snapshot[key] : fallback;
  const chargesEnabled = !conflict && storedReady && (isFinix || liveStatus?.chargesEnabled !== false);
  const payoutsEnabled = isFinix ? null : read('payoutsEnabled', merchant?.stripeDetails?.payoutsEnabled ?? null);
  const detailsSubmitted = read('detailsSubmitted', merchant?.stripeDetails?.detailsSubmitted) === true;
  const due = read('requirementsCurrentlyDue', merchant?.stripeDetails?.verificationEmail?.lastCurrentlyDue ?? []);
  const pastDue = read('requirementsPastDue', []);
  const pending = read('requirementsPendingVerification', []);
  const disabledReason = read('requirementsDisabledReason');
  const hasPending = pending?.length > 0 || disabledReason === 'requirements.pending_verification';
  const additionalInformationRequired = !isFinix && (due?.length > 0 || pastDue?.length > 0)
    && (setupStarted || detailsSubmitted || storedReady || liveStatus?.chargesEnabled === true || hasPending || pastDue?.length > 0);
  const confirmationPending = !isFinix && !conflict && !storedReady && liveStatus?.chargesEnabled === true;
  const restricted = disabledReason && !['requirements.pending_verification', 'requirements.past_due', 'requirements.fields_needed'].includes(disabledReason);
  const lastCheckedAt = read('lastCheckedAt');
  const lastWebhookEventAt = read('lastWebhookEventAt', merchant?.stripeDetails?.lastWebhookEventAt ?? null);
  const checkedMs = lastCheckedAt ? Date.parse(lastCheckedAt) : NaN;
  const stale = !isFinix && (checkFailed || (Number.isFinite(checkedMs) && now - checkedMs > STATUS_STALE_AFTER_MS));
  let status;
  if (conflict) status = STRIPE_SETUP.UNAVAILABLE;
  else if (additionalInformationRequired) status = STRIPE_SETUP.ACTION_REQUIRED;
  else if (chargesEnabled) status = payoutsEnabled === false ? STRIPE_SETUP.PAYOUT_PENDING : STRIPE_SETUP.READY;
  else if (confirmationPending) status = syncDelayed ? STRIPE_SETUP.SYNC_DELAYED : STRIPE_SETUP.APPROVAL_SYNCING;
  else if (restricted) status = STRIPE_SETUP.RESTRICTED;
  else if (checkFailed && !liveStatus && !Object.keys(snapshot).length) status = STRIPE_SETUP.UNAVAILABLE;
  else if (hasPending) status = STRIPE_SETUP.REVIEW_PENDING;
  else if (detailsSubmitted) status = STRIPE_SETUP.SUBMITTED;
  else status = setupStarted ? STRIPE_SETUP.INCOMPLETE : STRIPE_SETUP.NOT_STARTED;
  const unknownMerchant = !merchant || merchant.__charitystackRouterSeed === true;
  const unknownReadiness = !isFinix && getStripeAccountId(merchant) && typeof merchant?.stripeDetails?.chargesEnabled !== 'boolean' && !liveStatus && !checkFailed;
  const unconfirmedSetup = [STRIPE_SETUP.NOT_STARTED, STRIPE_SETUP.INCOMPLETE].includes(status);
  const waitingForInitialSetupCheck = initialCheckPending && unconfirmedSetup;
  if (unknownMerchant || (!conflict && unknownReadiness && unconfirmedSetup) || waitingForInitialSetupCheck) status = STRIPE_SETUP.CHECKING;
  return {
    status, chargesEnabled: unknownMerchant ? false : chargesEnabled, payoutsEnabled, additionalInformationRequired, confirmationPending,
    reviewPending: status === STRIPE_SETUP.REVIEW_PENDING, detailsSubmitted,
    disabledReason, requirementsCurrentDeadline: read('requirementsCurrentDeadline'),
    lastCheckedAt, lastWebhookEventAt, stale, accountConflict: conflict,
  };
}
